import { BuildCancelledError } from "@map2threejs/core/errors";
import { combineAttributions } from "@map2threejs/core/attribution";
import {
  chooseImageryZoom,
  normalizeTileX,
  tileRangesForRegion,
} from "@map2threejs/core/geo";
import type {
  BuildingContext,
  BuildingEntranceFeature,
  BuildingFeature,
  HeightTile,
  HeightTileSet,
  LinearFeature,
  MapFeatureSet,
  MapDataProvider,
  ProviderCapabilities,
  RasterTile,
  RasterTileSet,
  RegionRequest,
  RoadFeature,
  StreetObjectFeature,
  SurfaceFeature,
} from "@map2threejs/core/types";
import { TileDecoderClient } from "./decoder-client";
import { ProviderRequestError, RequestScheduler } from "./network";

interface TileJson {
  tilejson?: string;
  name?: string;
  tiles: string[];
  minzoom?: number;
  maxzoom?: number;
  format?: string;
  attribution?: string;
  bounds?: [number, number, number, number];
  tileSize?: number;
}

interface DiscoveredSources {
  imagery: TileJson;
  terrain: TileJson;
  buildings: TileJson;
  vector: TileJson;
}

const DEFAULT_ATTRIBUTION = "© MapTiler © OpenStreetMap contributors";

function tileSize(source: TileJson, fallback = 256): number {
  return Number.isFinite(source.tileSize) && (source.tileSize ?? 0) > 0 ? (source.tileSize as number) : fallback;
}

function addKey(url: string, key: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return /[?&]key=/.test(url) ? url : `${url}${separator}key=${encodeURIComponent(key)}`;
}

function expandTemplate(template: string, z: number, x: number, y: number, key: string): string {
  return addKey(
    template
      .replaceAll("{z}", String(z))
      .replaceAll("{x}", String(x))
      .replaceAll("{y}", String(y))
      .replaceAll("{ratio}", ""),
    key,
  );
}

export function mergeBuildingFeatures(features: BuildingFeature[]): BuildingFeature[] {
  const merged = new Map<string, BuildingFeature>();
  for (const feature of features) {
    const existing = merged.get(feature.id);
    if (!existing) {
      merged.set(feature.id, { ...feature, polygons: [...feature.polygons] });
      continue;
    }
    existing.polygons.push(...feature.polygons);
    if (existing.className === undefined && feature.className !== undefined) existing.className = feature.className;
    if (existing.subclass === undefined && feature.subclass !== undefined) existing.subclass = feature.subclass;
    if (feature.labelMetadata) existing.labelMetadata = true;
    if (existing.groupId === undefined && feature.groupId !== undefined) existing.groupId = feature.groupId;
    if (feature.underground) existing.underground = true;
    if (existing.heightMeters === undefined && feature.heightMeters !== undefined) existing.heightMeters = feature.heightMeters;
    if (existing.minimumHeightMeters === undefined && feature.minimumHeightMeters !== undefined) {
      existing.minimumHeightMeters = feature.minimumHeightMeters;
    }
    if (existing.facadeColor === undefined && feature.facadeColor !== undefined) existing.facadeColor = feature.facadeColor;
    if (existing.roofColor === undefined && feature.roofColor !== undefined) existing.roofColor = feature.roofColor;
    if (existing.roofShape === undefined && feature.roofShape !== undefined) existing.roofShape = feature.roofShape;
  }
  return [...merged.values()];
}

function mergeRoadFeatures(features: RoadFeature[]): RoadFeature[] {
  const merged = new Map<string, RoadFeature>();
  for (const feature of features) {
    const existing = merged.get(feature.id);
    if (!existing) {
      merged.set(feature.id, { ...feature, lines: [...feature.lines], polygons: [...feature.polygons] });
      continue;
    }
    existing.lines.push(...feature.lines);
    existing.polygons.push(...feature.polygons);
    if (existing.sourceLayer === undefined && feature.sourceLayer !== undefined) existing.sourceLayer = feature.sourceLayer;
    if (existing.subclass === undefined && feature.subclass !== undefined) existing.subclass = feature.subclass;
    if (existing.brunnel === undefined && feature.brunnel !== undefined) existing.brunnel = feature.brunnel;
    if (existing.layer === undefined && feature.layer !== undefined) existing.layer = feature.layer;
    if (existing.paved === undefined && feature.paved !== undefined) existing.paved = feature.paved;
    if (existing.oneway === undefined && feature.oneway !== undefined) existing.oneway = feature.oneway;
    if (existing.access === undefined && feature.access !== undefined) existing.access = feature.access;
    if (existing.service === undefined && feature.service !== undefined) existing.service = feature.service;
    if (existing.ramp === undefined && feature.ramp !== undefined) existing.ramp = feature.ramp;
    if (existing.construction === undefined && feature.construction !== undefined) existing.construction = feature.construction;
    if (existing.indoor === undefined && feature.indoor !== undefined) existing.indoor = feature.indoor;
    if (existing.level === undefined && feature.level !== undefined) existing.level = feature.level;
  }
  return [...merged.values()];
}

function mergeLinearFeatures(features: LinearFeature[]): LinearFeature[] {
  const merged = new Map<string, LinearFeature>();
  for (const feature of features) {
    const existing = merged.get(feature.id);
    if (!existing) {
      merged.set(feature.id, { ...feature, lines: [...feature.lines] });
      continue;
    }
    existing.lines.push(...feature.lines);
  }
  return [...merged.values()];
}

function mergeSurfaceFeatures(features: SurfaceFeature[]): SurfaceFeature[] {
  const merged = new Map<string, SurfaceFeature>();
  for (const feature of features) {
    const existing = merged.get(feature.id);
    if (!existing) {
      merged.set(feature.id, { ...feature, polygons: [...feature.polygons] });
      continue;
    }
    existing.polygons.push(...feature.polygons);
  }
  return [...merged.values()];
}

export function mergeStreetObjects(features: StreetObjectFeature[]): StreetObjectFeature[] {
  const merged = new Map<string, StreetObjectFeature>();
  const spatialKeys = new Set<string>();
  for (const feature of features) {
    const spatialKey = [
      feature.sourceLayer ?? "",
      feature.subclass ?? feature.className,
      feature.position.lat.toFixed(6),
      feature.position.lng.toFixed(6),
    ].join(":");
    if (merged.has(feature.id) || spatialKeys.has(spatialKey)) continue;
    merged.set(feature.id, feature);
    spatialKeys.add(spatialKey);
  }
  return [...merged.values()];
}

function mergeBuildingEntrances(features: BuildingEntranceFeature[]): BuildingEntranceFeature[] {
  const merged = new Map<string, BuildingEntranceFeature>();
  for (const feature of features) {
    if (!merged.has(feature.id)) merged.set(feature.id, feature);
  }
  return [...merged.values()];
}

export class MapTilerProvider implements MapDataProvider {
  private readonly scheduler = new RequestScheduler(6);
  private readonly decoder = new TileDecoderClient();
  private discovered: DiscoveredSources | undefined;
  private capabilities: ProviderCapabilities | undefined;

  constructor(private readonly apiKey: string) {
    if (!apiKey.trim()) throw new Error("A MapTiler API key is required.");
  }

  private async loadTileJson(id: string, signal: AbortSignal): Promise<TileJson> {
    const response = await this.scheduler.fetch(
      `https://api.maptiler.com/tiles/${id}/tiles.json?key=${encodeURIComponent(this.apiKey)}`,
      `${id} TileJSON`,
      signal,
    );
    const value = (await response.json()) as Partial<TileJson>;
    if (!Array.isArray(value.tiles) || value.tiles.length === 0) {
      throw new Error(`${id} TileJSON did not contain a usable tile template.`);
    }
    return value as TileJson;
  }

  async validate(signal: AbortSignal): Promise<ProviderCapabilities> {
    if (signal.aborted) throw new BuildCancelledError();
    if (this.capabilities) return this.capabilities;
    const [imagery, terrain, buildings, vector] = await Promise.all([
      this.loadTileJson("satellite-v2", signal),
      this.loadTileJson("terrain-rgb-v2", signal),
      this.loadTileJson("buildings", signal),
      this.loadTileJson("v4", signal),
    ]);
    this.discovered = { imagery, terrain, buildings, vector };
    this.capabilities = {
      imagery: {
        minZoom: imagery.minzoom ?? 0,
        maxZoom: imagery.maxzoom ?? 22,
        // satellite-v2 serves 512 px JPEG tiles even though its TileJSON does
        // not currently declare tileSize. Using the generic 256 fallback
        // silently downsampled every source tile during mosaic generation.
        tileSize: tileSize(imagery, 512),
        ...(imagery.format ? { format: imagery.format } : {}),
      },
      terrain: {
        minZoom: terrain.minzoom ?? 0,
        maxZoom: terrain.maxzoom ?? 14,
        tileSize: tileSize(terrain),
        ...(terrain.format ? { format: terrain.format } : {}),
        horizontalResolutionMeters: 30,
        verticalResolutionMeters: 6,
      },
      buildings: {
        minZoom: buildings.minzoom ?? 12,
        maxZoom: buildings.maxzoom ?? 15,
        tileSize: tileSize(buildings),
        ...(buildings.format ? { format: buildings.format } : {}),
      },
      vector: {
        minZoom: vector.minzoom ?? 0,
        maxZoom: vector.maxzoom ?? 15,
        tileSize: tileSize(vector),
        ...(vector.format ? { format: vector.format } : {}),
      },
      attribution: combineAttributions(
        imagery.attribution,
        terrain.attribution,
        buildings.attribution,
        vector.attribution,
        DEFAULT_ATTRIBUTION,
      ),
    };
    return this.capabilities;
  }

  private async sources(signal: AbortSignal): Promise<{ sources: DiscoveredSources; capabilities: ProviderCapabilities }> {
    const capabilities = await this.validate(signal);
    if (!this.discovered) throw new Error("MapTiler source discovery did not complete.");
    return { sources: this.discovered, capabilities };
  }

  async loadImagery(request: RegionRequest): Promise<RasterTileSet> {
    const { sources, capabilities } = await this.sources(request.signal);
    const choice = chooseImageryZoom({
      center: request.center,
      radiusMeters: request.radiusMeters,
      minZoom: capabilities.imagery.minZoom,
      maxZoom: capabilities.imagery.maxZoom,
      tileSize: capabilities.imagery.tileSize,
      targetMetersPerPixel: request.quality.targetImageryMetersPerPixel,
      maxTextureSize: request.quality.maxTextureSize,
      maxTiles: request.quality.maxRasterTiles,
    });
    const range = tileRangesForRegion(request.center, request.radiusMeters, choice.zoom)[0];
    const template = sources.imagery.tiles[0];
    if (!range || !template) throw new Error("Satellite imagery tile coverage could not be resolved.");
    const tiles: Promise<RasterTile>[] = [];
    for (let y = range.minY; y <= range.maxY; y += 1) {
      for (let unwrappedX = range.minX; unwrappedX <= range.maxX; unwrappedX += 1) {
        const x = normalizeTileX(unwrappedX, choice.zoom);
        const column = unwrappedX - range.minX;
        const row = y - range.minY;
        tiles.push(
          this.scheduler
            .fetch(expandTemplate(template, choice.zoom, x, y, this.apiKey), "Satellite imagery tile", request.signal)
            .then(async (response) => {
              if (response.status === 204) {
                throw new ProviderRequestError("Required satellite imagery is not available for the complete region (204).", "coverage", 204);
              }
              return {
                z: choice.zoom,
                x,
                y,
                column,
                row,
                image: await response.blob(),
              };
            }),
        );
      }
    }
    return {
      zoom: choice.zoom,
      tileSize: capabilities.imagery.tileSize,
      columns: choice.columns,
      rows: choice.rows,
      originTileX: range.minX,
      originTileY: range.minY,
      metersPerPixel: choice.metersPerPixel,
      tiles: await Promise.all(tiles),
      attribution: combineAttributions(sources.imagery.attribution, DEFAULT_ATTRIBUTION),
    };
  }

  async loadTerrain(request: RegionRequest): Promise<HeightTileSet> {
    const { sources, capabilities } = await this.sources(request.signal);
    const zoom = capabilities.terrain.maxZoom;
    const range = tileRangesForRegion(request.center, request.radiusMeters, zoom)[0];
    const template = sources.terrain.tiles[0];
    if (!range || !template) throw new Error("Terrain tile coverage could not be resolved.");
    const tasks: Promise<HeightTile>[] = [];
    for (let y = range.minY; y <= range.maxY; y += 1) {
      for (let unwrappedX = range.minX; unwrappedX <= range.maxX; unwrappedX += 1) {
        const x = normalizeTileX(unwrappedX, zoom);
        tasks.push(
          this.scheduler
            .fetch(expandTemplate(template, zoom, x, y, this.apiKey), "Terrain tile", request.signal)
            .then(async (response) => {
              if (response.status === 204) {
                throw new ProviderRequestError("Required terrain is not available for the complete region (204).", "coverage", 204);
              }
              const decoded = await this.decoder.decodeTerrain(await response.blob(), request.signal);
              return { z: zoom, x, y, ...decoded };
            }),
        );
      }
    }
    return {
      zoom,
      tileSize: capabilities.terrain.tileSize,
      tiles: await Promise.all(tasks),
      horizontalResolutionMeters: capabilities.terrain.horizontalResolutionMeters,
      verticalResolutionMeters: capabilities.terrain.verticalResolutionMeters,
      attribution: combineAttributions(sources.terrain.attribution, DEFAULT_ATTRIBUTION),
    };
  }

  async loadBuildings(request: RegionRequest): Promise<BuildingFeature[]> {
    return (await this.loadBuildingContext(request)).buildings;
  }

  async loadBuildingContext(request: RegionRequest): Promise<BuildingContext> {
    const { sources } = await this.sources(request.signal);
    const zoom = 15;
    const range = tileRangesForRegion(request.center, request.radiusMeters, zoom)[0];
    const template = sources.buildings.tiles[0];
    if (!range || !template) throw new Error("Building tile coverage could not be resolved.");
    const tasks: Promise<BuildingContext>[] = [];
    for (let y = range.minY; y <= range.maxY; y += 1) {
      for (let unwrappedX = range.minX; unwrappedX <= range.maxX; unwrappedX += 1) {
        const x = normalizeTileX(unwrappedX, zoom);
        tasks.push(
          this.scheduler
            .fetch(expandTemplate(template, zoom, x, y, this.apiKey), "Building vector tile", request.signal)
            .then(async (response) =>
              response.status === 204
                ? { buildings: [], entrances: [] }
                : this.decoder.decodeBuildingContext(await response.arrayBuffer(), zoom, x, y, request.signal),
            ),
        );
      }
    }
    const decoded = await Promise.all(tasks);
    return {
      buildings: mergeBuildingFeatures(decoded.flatMap((tile) => tile.buildings)),
      entrances: mergeBuildingEntrances(decoded.flatMap((tile) => tile.entrances)),
    };
  }

  async loadMapFeatures(request: RegionRequest): Promise<MapFeatureSet> {
    const { sources, capabilities } = await this.sources(request.signal);
    const zoom = Math.min(15, capabilities.vector.maxZoom);
    const range = tileRangesForRegion(request.center, request.radiusMeters, zoom)[0];
    const template = sources.vector.tiles[0];
    if (!range || !template) throw new Error("Map vector tile coverage could not be resolved.");
    const tasks: Promise<Pick<MapFeatureSet, "roads" | "waterways" | "barriers" | "infrastructure" | "surfaces" | "streetObjects">>[] = [];
    for (let y = range.minY; y <= range.maxY; y += 1) {
      for (let unwrappedX = range.minX; unwrappedX <= range.maxX; unwrappedX += 1) {
        const x = normalizeTileX(unwrappedX, zoom);
        tasks.push(
          this.scheduler
            .fetch(expandTemplate(template, zoom, x, y, this.apiKey), "Map vector tile", request.signal)
            .then(async (response) => response.status === 204
              ? { roads: [], waterways: [], barriers: [], infrastructure: [], surfaces: [], streetObjects: [] }
              : this.decoder.decodeMapFeatures(await response.arrayBuffer(), zoom, x, y, request.signal)),
        );
      }
    }
    const decoded = await Promise.all(tasks);
    return {
      zoom,
      roads: mergeRoadFeatures(decoded.flatMap((tile) => tile.roads)),
      waterways: mergeLinearFeatures(decoded.flatMap((tile) => tile.waterways)),
      barriers: mergeLinearFeatures(decoded.flatMap((tile) => tile.barriers)),
      infrastructure: mergeLinearFeatures(decoded.flatMap((tile) => tile.infrastructure ?? [])),
      surfaces: mergeSurfaceFeatures(decoded.flatMap((tile) => tile.surfaces)),
      streetObjects: mergeStreetObjects(decoded.flatMap((tile) => tile.streetObjects)),
      attribution: combineAttributions(sources.vector.attribution, DEFAULT_ATTRIBUTION),
    };
  }

  dispose(): void {
    this.decoder.dispose();
    this.discovered = undefined;
    this.capabilities = undefined;
  }
}
